import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, Unlink, UserPlus, AlertTriangle, CheckCircle2 } from 'lucide-react';

const fmtCLP = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-CL')}`;

export default function AuditoriaTrazabilidad() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(null);
  const [asignarDlg, setAsignarDlg] = useState({ open: false, numero_oc: null });
  const [provQuery, setProvQuery] = useState('');
  const [provResultados, setProvResultados] = useState([]);
  const [provSel, setProvSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/compras/trazabilidad/auditoria');
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando auditoría');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const buscarProveedores = async (q) => {
    setProvQuery(q);
    setProvSel(null);
    if (!q || q.length < 2) { setProvResultados([]); return; }
    try {
      const res = await api.get(`/compras/proveedores-lista?q=${encodeURIComponent(q)}`);
      setProvResultados((res.data?.proveedores || res.data?.items || res.data || []).slice(0, 8));
    } catch { setProvResultados([]); }
  };

  const desasociar = async (row) => {
    const ok = window.confirm(
      `¿Desasociar el DTE folio ${row.folio} de ${row.dte_proveedor} de la OC ${row.numero_oc} (${row.oc_proveedor})?\n\n` +
      'El DTE quedará sin OC y podrás asociarle la correcta después.'
    );
    if (!ok) return;
    setProcesando(`${row.folio}|${row.numero_oc}`);
    try {
      await api.post('/compras/trazabilidad/desasociar-dte-oc', {
        folio: row.folio, rut_emisor: row.dte_rut, numero_oc: row.numero_oc,
      });
      toast.success(`DTE ${row.folio} desasociado de la OC ${row.numero_oc}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al desasociar');
    } finally { setProcesando(null); }
  };

  const asignarProveedor = async () => {
    if (!provSel) { toast.error('Selecciona un proveedor de la lista'); return; }
    const rut = (provSel.rut || provSel.identificacion || '').trim();
    if (!rut) { toast.error('El proveedor seleccionado no tiene RUT — complétalo en el maestro primero'); return; }
    setProcesando(`asignar|${asignarDlg.numero_oc}`);
    try {
      await api.put('/compras/trazabilidad/asignar-proveedor-oc', {
        numero_oc: asignarDlg.numero_oc, rut_proveedor: rut, nombre_proveedor: provSel.nombre || '',
      });
      toast.success(`Proveedor asignado a la OC ${asignarDlg.numero_oc}`);
      setAsignarDlg({ open: false, numero_oc: null });
      setProvQuery(''); setProvResultados([]); setProvSel(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al asignar proveedor');
    } finally { setProcesando(null); }
  };

  const resumen = data?.resumen || {};
  const cruzados = data?.dte_oc_rut_cruzado || [];
  const ocsSinProv = data?.ocs_sin_proveedor?.recientes || [];
  const billsMismatch = data?.bills_dte_mismatch || [];

  return (
    <div className="space-y-5" data-testid="auditoria-trazabilidad-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" /> Auditoría de Trazabilidad
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Requisición → OC → Recepción → DTE → Contabilización → Pago. Detecta OCs sin proveedor,
            DTEs asociados a OC de otro proveedor y facturas de Alegra que no calzan con su DTE.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="btn-refrescar-auditoria">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="card-ocs-sin-proveedor">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500">OCs sin proveedor (desde 2026)</p>
            <p className={`text-3xl font-bold ${ocsSinProv.length ? 'text-amber-600' : 'text-emerald-600'}`}>{loading ? '…' : ocsSinProv.length}</p>
            <p className="text-[11px] text-gray-400">Histórico total: {data?.ocs_sin_proveedor?.total_historico ?? '…'}</p>
          </CardContent>
        </Card>
        <Card data-testid="card-dte-oc-cruzados">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500">DTEs con OC de otro proveedor</p>
            <p className={`text-3xl font-bold ${cruzados.length ? 'text-red-600' : 'text-emerald-600'}`}>{loading ? '…' : cruzados.length}</p>
            <p className="text-[11px] text-gray-400">Referencias de OC erróneas heredadas del XML</p>
          </CardContent>
        </Card>
        <Card data-testid="card-bills-mismatch">
          <CardContent className="pt-5">
            <p className="text-xs text-gray-500">Facturas Alegra que no calzan con DTE</p>
            <p className={`text-3xl font-bold ${billsMismatch.length ? 'text-red-600' : 'text-emerald-600'}`}>{loading ? '…' : billsMismatch.length}</p>
            <p className="text-[11px] text-gray-400">Bills abiertas: folio+RUT vs DTE</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            DTEs asociados a una OC de OTRO proveedor ({cruzados.length})
          </CardTitle>
          <p className="text-xs text-gray-500">
            El proveedor escribió en su factura una referencia de OC que corresponde a otro proveedor nuestro.
            Revisa y desasocia los erróneos; luego asocia la OC correcta desde Facturas por Contabilizar.
          </p>
        </CardHeader>
        <CardContent>
          {cruzados.length === 0 && !loading ? (
            <p className="text-sm text-emerald-600 flex items-center gap-1" data-testid="cruzados-vacio"><CheckCircle2 className="w-4 h-4" /> Sin cruces detectados</p>
          ) : (
            <Table data-testid="tabla-cruzados">
              <TableHeader>
                <TableRow>
                  <TableHead>Folio</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Proveedor del DTE</TableHead>
                  <TableHead>OC</TableHead>
                  <TableHead>Proveedor de la OC</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cruzados.map((r) => (
                  <TableRow key={`${r.folio}-${r.dte_rut}-${r.numero_oc}`}>
                    <TableCell className="font-mono">{r.folio}</TableCell>
                    <TableCell><Badge variant="outline">{r.tipo_dte === '52' ? 'Guía' : r.tipo_dte === '61' ? 'NC' : 'Factura'}</Badge></TableCell>
                    <TableCell>{r.fecha_emision}</TableCell>
                    <TableCell className="max-w-[220px] truncate" title={`${r.dte_proveedor} (${r.dte_rut})`}>
                      {r.dte_proveedor}
                      {r.es_venta_propia && (
                        <Badge className="ml-1 bg-blue-100 text-blue-700" title="Factura de VENTA emitida por Chirino — la referencia de OC es la OC del cliente, no una OC interna">venta propia</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">{r.numero_oc}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-red-600" title={r.oc_proveedor}>{r.oc_proveedor}</TableCell>
                    <TableCell className="text-right">{fmtCLP(r.total)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm" variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        disabled={procesando === `${r.folio}|${r.numero_oc}`}
                        onClick={() => desasociar(r)}
                        data-testid={`btn-desasociar-${r.folio}`}
                      >
                        <Unlink className="w-3.5 h-3.5 mr-1" /> Desasociar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            OCs sin proveedor — desde 2026 ({ocsSinProv.length})
          </CardTitle>
          <p className="text-xs text-gray-500">Asigna el proveedor directamente aquí (funciona aunque la OC ya esté aprobada).</p>
        </CardHeader>
        <CardContent>
          {ocsSinProv.length === 0 && !loading ? (
            <p className="text-sm text-emerald-600 flex items-center gap-1" data-testid="ocs-sin-prov-vacio"><CheckCircle2 className="w-4 h-4" /> Todas las OCs recientes tienen proveedor</p>
          ) : (
            <Table data-testid="tabla-ocs-sin-proveedor">
              <TableHeader>
                <TableRow>
                  <TableHead>OC</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Nombre registrado</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Creada por</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ocsSinProv.map((oc) => (
                  <TableRow key={oc.numero_oc}>
                    <TableCell className="font-mono">{oc.numero_oc}</TableCell>
                    <TableCell>{oc.fecha_norm}</TableCell>
                    <TableCell className="max-w-[220px] truncate">{oc.nombre_proveedor || <span className="text-gray-400">(sin nombre)</span>}</TableCell>
                    <TableCell className="text-right">{fmtCLP(oc.total)}</TableCell>
                    <TableCell className="text-xs">{oc.creado_por || '—'}</TableCell>
                    <TableCell>{oc.aprobada ? <Badge className="bg-emerald-100 text-emerald-700">Aprobada</Badge> : <Badge variant="outline">Pendiente</Badge>}</TableCell>
                    <TableCell>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setAsignarDlg({ open: true, numero_oc: oc.numero_oc }); setProvQuery(''); setProvResultados([]); setProvSel(null); }}
                        data-testid={`btn-asignar-prov-${oc.numero_oc}`}
                      >
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Asignar proveedor
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {billsMismatch.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              Facturas de Alegra que no calzan con su DTE ({billsMismatch.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table data-testid="tabla-bills-mismatch">
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead><TableHead>Folio</TableHead><TableHead>Proveedor bill</TableHead>
                  <TableHead className="text-right">Total bill</TableHead><TableHead>Detalle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {billsMismatch.map((b) => (
                  <TableRow key={`${b.alegra_id}-${b.folio}`}>
                    <TableCell><Badge variant="destructive">{b.tipo}</Badge></TableCell>
                    <TableCell className="font-mono">{b.folio}</TableCell>
                    <TableCell>{b.proveedor}</TableCell>
                    <TableCell className="text-right">{fmtCLP(b.total_bill)}</TableCell>
                    <TableCell className="text-xs">{b.tipo === 'monto_difiere' ? `DTE: ${fmtCLP(b.total_dte)}` : `DTE emitido por: ${(b.dte_emisores || []).join(', ')}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={asignarDlg.open} onOpenChange={(o) => !o && setAsignarDlg({ open: false, numero_oc: null })}>
        <DialogContent data-testid="dialog-asignar-proveedor">
          <DialogHeader>
            <DialogTitle>Asignar proveedor a la OC {asignarDlg.numero_oc}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Busca por nombre o RUT…"
              value={provQuery}
              onChange={(e) => buscarProveedores(e.target.value)}
              data-testid="input-buscar-proveedor-auditoria"
            />
            <div className="max-h-56 overflow-y-auto divide-y rounded border">
              {provResultados.map((p) => {
                const rut = p.rut || p.identificacion || '';
                const sel = provSel && (provSel.id === p.id || (provSel.rut || provSel.identificacion) === rut);
                return (
                  <button
                    key={p.id || rut || p.nombre}
                    type="button"
                    onClick={() => setProvSel(p)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${sel ? 'bg-blue-100' : ''}`}
                    data-testid={`opcion-proveedor-${rut || p.nombre}`}
                  >
                    <span className="font-medium">{p.nombre}</span>
                    <span className={`ml-2 text-xs ${rut ? 'text-gray-500' : 'text-red-500'}`}>{rut || 'SIN RUT'}</span>
                  </button>
                );
              })}
              {provQuery.length >= 2 && provResultados.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-400">Sin resultados</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAsignarDlg({ open: false, numero_oc: null })}>Cancelar</Button>
              <Button
                onClick={asignarProveedor}
                disabled={!provSel || procesando === `asignar|${asignarDlg.numero_oc}`}
                data-testid="btn-confirmar-asignar-proveedor"
              >
                Asignar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <div className="pb-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/compras/ordenes-compra')} className="text-gray-500">
          Ver listado completo de OCs →
        </Button>
      </div>
    </div>
  );
}
